import { Pipette } from "lucide-react";
import * as React from "react";
import { HexColorInput, HexColorPicker } from "react-colorful";

import { getRecentColors, pushRecentColor } from "../lib/colorRecents";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/** Minimal type for the native EyeDropper API (not yet in the TS DOM lib).
 *  Feature-detected at runtime — the control hides when unavailable. */
interface EyeDropperCtor {
  new (): { open: () => Promise<{ sRGBHex: string }> };
}

/**
 * Built-in preset swatches. The first is always `transparent` (checkerboard);
 * the rest are the common picks from QA. Consumers can override via `presets`.
 */
const DEFAULT_PRESETS: readonly string[] = [
  "transparent",
  "#000000",
  "#ffffff",
  "#0b5919", // brand green
  "#6b7280", // muted gray
  "#dc2626", // red
  "#16a34a", // green
  "#2563eb", // blue
];

const HEX_REGEX = /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;

function normalizeHex(input: string): string | null {
  const trimmed = input.trim();
  if (!HEX_REGEX.test(trimmed)) return null;
  const hex = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  const expanded =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  return `#${expanded.toLowerCase()}`;
}

/** True when `value` is the CSS `transparent` keyword or a 0-alpha rgba(). */
function isTransparent(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v === "transparent") return true;
  if (/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0(?:\.0+)?\s*\)$/.test(v)) return true;
  return false;
}

/** The spectrum picker only understands hex. Coerce the current value to a hex
 *  the wheel can display; `transparent` / CSS vars / rgb() anchor on a neutral. */
function spectrumHex(value: string): string {
  if (isTransparent(value)) return "#ffffff";
  return normalizeHex(value) ?? "#000000";
}

/** Checkerboard fill for a transparent swatch — a CSS gradient, no asset. */
const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(45deg, #c0c0c0 25%, transparent 25%), " +
    "linear-gradient(-45deg, #c0c0c0 25%, transparent 25%), " +
    "linear-gradient(45deg, transparent 75%, #c0c0c0 75%), " +
    "linear-gradient(-45deg, transparent 75%, #c0c0c0 75%)",
  backgroundSize: "8px 8px",
  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0",
  backgroundColor: "#ffffff",
};

type ColorPickerProps = {
  value: string;
  onChange: (color: string) => void;
  onReset?: () => void;
  resetLabel?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  "aria-label"?: string;
  /** Override the built-in preset swatch list. Pass `[]` to hide presets. */
  presets?: readonly string[];
  /**
   * SIM-1165 no-lying-defaults: the resolved color shown in the trigger swatch
   * (override OR default). Defaults to `value` when omitted.
   */
  resolvedValue?: string;
};

function SwatchButton({
  color,
  selected,
  label,
  disabled,
  onSelect,
  className,
}: {
  color: string;
  selected?: boolean;
  label?: string;
  disabled?: boolean;
  onSelect: () => void;
  className?: string;
}) {
  const transparent = isTransparent(color);
  const resolvedLabel = label ?? (transparent ? "Transparent" : color);
  return (
    <button
      type="button"
      aria-label={resolvedLabel}
      title={resolvedLabel}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "h-6 w-6 shrink-0 rounded-md border border-input transition-shadow",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        "disabled:cursor-not-allowed disabled:opacity-50",
        selected && "ring-2 ring-ring ring-offset-1",
        className,
      )}
      style={transparent ? CHECKERBOARD_STYLE : { backgroundColor: color }}
    />
  );
}

/**
 * SIM-2249: a diagonal slash drawn over a DISABLED color swatch, so a greyed
 * swatch reads as "unavailable" rather than "the color was just changed to
 * grey" (QA). A light under-stroke + darker over-stroke keeps the diagonal
 * visible on any swatch background.
 */
function DisabledSlash() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      <svg
        viewBox="0 0 24 24"
        preserveAspectRatio="none"
        className="h-full w-full text-muted-foreground"
      >
        <line x1="0" y1="24" x2="24" y2="0" stroke="white" strokeWidth="3" opacity="0.7" />
        <line x1="0" y1="24" x2="24" y2="0" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    </span>
  );
}

/**
 * SIM-2252: screen eyedropper via the native `EyeDropper` API. Feature-detected
 * — returns `null` where unsupported (Firefox/Safari). Rendered as the last
 * item in the recents row.
 */
function EyedropperSwatch({
  disabled,
  onPick,
}: {
  disabled: boolean | undefined;
  onPick: (hex: string) => void;
}) {
  const Ctor = (globalThis as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  if (!Ctor) return null;
  const open = () => {
    new Ctor()
      .open()
      .then((r) => onPick(r.sRGBHex))
      .catch(() => {
        // cancelled — no-op
      });
  };
  return (
    <button
      type="button"
      aria-label="Pick a color from the screen"
      title="Eyedropper"
      disabled={disabled}
      onClick={open}
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-input text-muted-foreground",
        "hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      <Pipette className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

/**
 * SIM-2252: the full color-selection panel inside the picker popover — a spectrum wheel
 * (react-colorful), the preset swatches, and a recents row ending in the eyedropper. Emits
 * `transparent` for the transparent preset and a hex for everything else.
 */
function ColorPanel({
  value,
  onChange,
  disabled,
  presets = DEFAULT_PRESETS,
}: Pick<ColorPickerProps, "value" | "onChange" | "disabled" | "presets">) {
  const normalizedValue = value.trim().toLowerCase();
  const [recents] = React.useState(() =>
    getRecentColors().filter((c) => !presets.some((p) => p.trim().toLowerCase() === c)),
  );
  const eyedropperAvailable = "EyeDropper" in globalThis;

  return (
    <div data-slot="color-panel" className="flex w-64 flex-col gap-2">
      <HexColorPicker
        color={spectrumHex(value)}
        onChange={disabled ? () => {} : onChange}
        style={{ width: "100%", height: 140 }}
      />
      {/* SIM-2252: hex entry for the chosen color, directly under the spectrum. */}
      <HexColorInput
        color={spectrumHex(value)}
        onChange={onChange}
        prefixed
        disabled={disabled}
        aria-label="Hex color value"
        className="h-7 w-full rounded-md border border-input bg-background px-2 text-sm uppercase focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />
      {presets.length > 0 && (
        <fieldset className="m-0 flex flex-wrap gap-1.5 border-0 p-0" data-testid="color-presets">
          <legend className="sr-only">Color presets</legend>
          {presets.map((preset) => (
            <SwatchButton
              key={preset}
              color={preset}
              selected={preset.trim().toLowerCase() === normalizedValue}
              disabled={disabled}
              onSelect={() => onChange(preset)}
            />
          ))}
        </fieldset>
      )}
      {(recents.length > 0 || eyedropperAvailable) && (
        <fieldset
          className="m-0 flex flex-wrap items-center gap-1.5 border-0 p-0"
          data-testid="color-recents"
        >
          <legend className="sr-only">Recent colors</legend>
          {recents.map((recent) => (
            <SwatchButton
              key={recent}
              color={recent}
              selected={recent === normalizedValue}
              disabled={disabled}
              onSelect={() => onChange(recent)}
            />
          ))}
          <EyedropperSwatch disabled={disabled} onPick={onChange} />
        </fieldset>
      )}
    </div>
  );
}

/**
 * SIM-2252: the canonical color control — a compact swatch trigger showing the current color that
 * opens `ColorPanel` in a popover. Used inline in config forms and in the context hotbars alike.
 * Records the settled color into the shared recents list when the popover closes.
 */
function ColorPicker({
  value,
  onChange,
  disabled,
  id,
  className,
  "aria-label": ariaLabel,
  presets,
  resolvedValue,
}: ColorPickerProps) {
  const swatchColor = resolvedValue ?? value;
  const transparent = isTransparent(swatchColor);
  return (
    <Popover onOpenChange={(open) => !open && pushRecentColor(value)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel ?? "Choose color"}
          className={cn(
            "relative h-7 w-9 shrink-0 overflow-hidden rounded-md border border-input transition-shadow",
            "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          style={transparent ? CHECKERBOARD_STYLE : { backgroundColor: swatchColor }}
        >
          {disabled && <DisabledSlash />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <ColorPanel value={value} onChange={onChange} disabled={disabled} presets={presets} />
      </PopoverContent>
    </Popover>
  );
}

export { ColorPanel, ColorPicker, DEFAULT_PRESETS, isTransparent, pushRecentColor };
export type { ColorPickerProps };
